// Plan tab (CI v2 home): the current step large in lamplight, the rest of the evening as tiles,
// the bedtime line, and the Shhh Baby Button. In the day the card shows the next nap instead.
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { clock, duration, MIN } from '@/lib/format';
import { useSession } from '@/lib/session';
import { BabyButton } from '@/features/sound/BabyButton';
import { NightModeLink } from '@/features/sounds/NightModeLink';
import { addEntry, stopTimer, syncNow } from '@/features/track/store';
import { TipCard } from '@/features/learn/TipCard';
import { STEP_LOOK, stepName, type Step } from './steps';
import { usePlan, type Routine } from './usePlan';
import './plan.css';
import { art } from '@/lib/skin';

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

export function PlanScreen() {
  const { baby, family } = useSession();
  const tz = family?.timezone;
  const { routine, setRoutine, forecast, runningSleep, error, reload } = usePlan(baby?.id);
  const [, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState('');
  const rowRef = useRef<HTMLOListElement>(null);
  const plateRef = useRef<HTMLImageElement>(null);

  useEffect(() => { const iv = window.setInterval(() => setTick((t) => t + 1), 30_000); return () => clearInterval(iv); }, []);

  const now = Date.now();
  const evening = !!routine && (routine.done.length > 0 || now >= Date.parse(routine.startAt) - 15 * MIN) && !(forecast?.state.status === 'asleep' && forecast.state.kind === 'nap');
  const current = routine?.steps.find((s) => s.id === routine.currentStepId) ?? null;
  const doneIds = new Set(routine?.done.map((d) => d.stepId));
  const upcoming: Step[] = routine ? (evening ? routine.steps.filter((s) => !doneIds.has(s.id) && s.id !== current?.id) : routine.steps) : [];

  // ---------- routine: complete / undo / restart ----------
  async function animateHandover(apply: () => void) {
    const t = reduced() ? 0 : 560;
    const row = rowRef.current;
    const first = row?.querySelector<HTMLElement>('.step');
    const moves: Animation[] = [];
    if (first && row) {
      const shift = first.getBoundingClientRect().width + parseFloat(getComputedStyle(row).columnGap || '0');
      moves.push(first.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-22px) scale(0.9)' }], { duration: t * 0.6, easing: EASE, fill: 'forwards' }));
      [...row.querySelectorAll<HTMLElement>('.step')].slice(1).forEach((el) => moves.push(el.animate([{ transform: 'none' }, { transform: `translateX(${-shift}px)` }], { duration: t, easing: EASE, fill: 'forwards' })));
    }
    const img = plateRef.current;
    if (img) await img.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(0.96)' }], { duration: t * 0.4, easing: 'ease-in', fill: 'forwards' }).finished;
    await Promise.all(moves.map((a) => a.finished));
    apply();
    moves.forEach((a) => a.cancel());
    requestAnimationFrame(() => {
      const im = plateRef.current;
      if (!im) return;
      im.getAnimations().forEach((a) => a.cancel());
      im.animate([{ opacity: 0, transform: 'translateY(10px) scale(1.03)' }, { opacity: 1, transform: 'none' }], { duration: t * 0.7, easing: EASE });
    });
  }

  async function completeStep() {
    if (!routine || !baby || busy) return;
    if (routine.finished) {
      setBusy(true);
      try { const r = await api<{ routine: Routine }>(`/api/babies/${baby.id}/routine/restart`, { method: 'POST', body: {} }); setRoutine(r.routine); setLive('Routine starts again'); }
      finally { setBusy(false); }
      return;
    }
    if (!current) return;
    setBusy(true);
    const optimistic: Routine = {
      ...routine,
      done: [...routine.done, { stepId: current.id, at: new Date().toISOString(), by: 'me' }],
      minutesLeft: routine.minutesLeft - current.minutes,
    };
    const next = routine.steps.find((s) => !optimistic.done.some((d) => d.stepId === s.id)) ?? null;
    optimistic.currentStepId = next?.id ?? null;
    optimistic.finished = !next;
    await animateHandover(() => { setRoutine(optimistic); setLive(next ? `Now: ${stepName(next)}${next.minutes ? `, ${next.minutes} min` : ''}` : 'Lights out'); });
    try {
      const r = await api<{ routine: Routine }>(`/api/babies/${baby.id}/routine/done`, { body: { stepId: current.id } });
      setRoutine(r.routine);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'not_current_step') void reload(); // the other parent moved on
      else setRoutine(routine);
    } finally {
      setBusy(false);
    }
  }

  async function undoStep() {
    if (!routine?.done.length || !baby || busy) return;
    setBusy(true);
    try {
      const r = await api<{ routine: Routine }>(`/api/babies/${baby.id}/routine/undo`, { method: 'POST', body: {} });
      const back = r.routine.steps.find((s) => s.id === r.routine.currentStepId);
      setRoutine(r.routine);
      setLive(back ? `Back to ${stepName(back)}` : '');
    } finally {
      setBusy(false);
    }
  }

  // ---------- day: start or end a nap ----------
  async function toggleNap() {
    if (!baby || busy) return;
    setBusy(true);
    try {
      // Saved on the phone first (works offline); the forecast refreshes once the change reaches the server.
      if (runningSleep) {
        await stopTimer(runningSleep.id);
        setLive(`Awake after ${duration(Date.now() - Date.parse(runningSleep.startAt))}`);
      } else {
        await addEntry(baby.id, { type: 'sleep', startAt: new Date().toISOString(), endAt: null, data: {} });
        setLive('Sleep timer started');
      }
      void syncNow().catch(() => undefined).then(() => reload());
    } catch {
      setLive('That did not save. Try again.');
    } finally {
      setBusy(false);
    }
  }

  // swipe right on the card undoes the last routine step
  const swipe = useRef<{ x: number; y: number; swiped: boolean } | null>(null);
  const onPointerDown = (e: React.PointerEvent) => { swipe.current = { x: e.clientX, y: e.clientY, swiped: false }; };
  const onPointerUp = (e: React.PointerEvent) => {
    const s = swipe.current;
    if (!s) return;
    const dx = e.clientX - s.x, dy = e.clientY - s.y;
    if (evening && dx > 50 && Math.abs(dy) < 40) { s.swiped = true; void undoStep(); }
  };
  const onCardClick = () => {
    if (swipe.current?.swiped) { swipe.current.swiped = false; return; }
    if (evening) void completeStep(); else void toggleNap();
  };

  // ---------- what the card says ----------
  let title = 'Bedtime routine';
  let plate = '/art/felt/track-nap.webp';
  let alt = 'a sleeping felt baby';
  let nowLine = '';
  let sub = '';
  let action = '';
  if (evening && routine) {
    if (current) {
      const look = STEP_LOOK[current.kind];
      plate = look.plate; alt = `${stepName(current)}: ${look.alt}`;
      nowLine = `Now · ${current.minutes ? `${current.minutes} min` : 'lights out'}`;
      const nextStep = routine.steps.find((s) => !doneIds.has(s.id) && s.id !== current.id);
      action = nextStep ? `${stepName(current)} done, start ${stepName(nextStep)}` : `${stepName(current)} done, lights out`;
    } else {
      plate = STEP_LOOK.bed.plate; alt = 'Bed: a felt crib';
      nowLine = 'Now · lights out';
      action = 'Start the routine again';
    }
  } else if (runningSleep || forecast?.state.status === 'asleep') {
    const since = runningSleep ? Date.parse(runningSleep.startAt) : forecast?.state.status === 'asleep' ? Date.parse(forecast.state.since) : now;
    title = forecast?.state.status === 'asleep' && forecast.state.kind === 'night' ? 'Night sleep' : 'Nap';
    nowLine = `Sleeping · ${duration(now - since)}`;
    sub = forecast?.state.status === 'asleep' ? `Wakes about ${clock(forecast.state.expectedWakeAt, tz)}` : '';
    action = `${baby?.name ?? 'Baby'} woke up: stop the sleep timer`;
  } else if (forecast?.nextSleep) {
    const at = Date.parse(forecast.nextSleep.at);
    const isBed = forecast.nextSleep.kind === 'bedtime';
    title = isBed ? 'Bedtime' : `Nap ${forecast.nextSleep.napNumber ?? ''}`.trim();
    nowLine = at > now ? `In ${duration(at - now)}` : 'Now';
    sub = `Around ${clock(at, tz)}`;
    action = `${baby?.name ?? 'Baby'} fell asleep: start the sleep timer`;
  } else {
    title = 'Sleep';
    nowLine = 'Log a sleep';
    sub = 'The plan learns from it';
    action = 'Start the sleep timer';
  }

  const bedtimeAt = routine ? Date.parse(routine.bedtime) : null;
  const toGo = bedtimeAt ? bedtimeAt - now : 0;

  return (
    <>
      <div className="lamp" aria-hidden="true"><img src="/art/plates/lamp.webp" alt="" /></div>
      <section className="now-card" aria-labelledby="plan-title">
        <h1 id="plan-title">{title}</h1>
        <img ref={plateRef} className="plate-main" src={plate} alt={alt} />
        <p className="now">{nowLine}</p>
        {sub && <p className="sub">{sub}</p>}
        <button className="card-done" type="button" aria-label={action} disabled={busy && !evening}
          onPointerDown={onPointerDown} onPointerUp={onPointerUp} onClick={onCardClick}
          onKeyDown={(e) => { if (e.key === 'Backspace' && evening) { e.preventDefault(); void undoStep(); } }} />
      </section>
      <p className="vh" aria-live="polite">{live}</p>

      <ol className="steps" ref={rowRef} aria-label={evening ? 'Next steps' : "Tonight's routine"}>
        {upcoming.map((s) => {
          const look = STEP_LOOK[s.kind];
          return (
            <li key={s.id} className={`step${s.kind === 'bed' ? ' is-bed' : ''}`} style={{ width: `${look.width}px`, ['--edge' as string]: look.edge }}>
              <img src={art(look.plate, `step-${s.kind}`)} alt="" style={{ left: look.img.left, top: look.img.top, width: look.img.width, height: look.img.height }} />
              <span className="name">{stepName(s)}</span>
              {s.kind !== 'bed' && <span className="dur">{s.minutes} min</span>}
            </li>
          );
        })}
      </ol>

      {bedtimeAt && (
        <p className="bedtime">
          Bedtime at {clock(bedtimeAt, tz)} · <span className="left">{toGo > 60_000 ? `${duration(toGo)} to go` : 'now'}</span>
        </p>
      )}
      {error && error !== 'offline' && <p className="plan-error">Could not refresh the plan. Pull down or try again later.</p>}
      {error === 'offline' && <p className="plan-error">Offline · showing the last plan</p>}
      <BabyButton><NightModeLink /></BabyButton>
      <TipCard variant="compact" />
    </>
  );
}
